"use client";

import React, { useEffect, useState, useCallback, useRef } from "react";
import Image from "next/image";
import { ChevronDownIcon } from "@heroicons/react/16/solid";

import BottomCurves from "../curves/bottomCurves";
import RevealElement from "../revealElement";

import { fetchAPI } from "@/utils/fetchData";
import { getStrapiMedia, slugify } from "@/utils/api-helpers";

type WorkTag = {
    id?: number | string;
    name?: string;
};

type WorkItem = {
    id?: number | string;
    name?: string;
    brandName?: string;
    slug?: string;
    tag?: string;
    designTags?: Array<string | WorkTag>;
    audienceTags?: Array<string | WorkTag>;
    image?: {
        url?: string;
        alternativeText?: string;
    };
};

type DisplayMode = "stack" | "grid";
type SelectOption<TValue extends string = string> = {
    label: string;
    value: TValue;
};
type RawWork = WorkItem & {
    attributes?: WorkItem;
};

const DESIGN_DEFAULT = "Everything";
const AUDIENCE_DEFAULT = "Everyone";
const DESIGN_TAG_OPTIONS = [
    "AI Creative Production",
    "Content Marketing",
    "Creative Automation",
    "Digital Marketing",
    "Experiential Marketing",
    "Influencer Marketing",
    "Integrated Marketing",
    "Social Media",
    "Video Production",
];
const AUDIENCE_TAG_OPTIONS = [
    "Automotive",
    "Aviation",
    "Baby & Infant Nutrition",
    "Banking",
    "Beauty & Cosmetics",
    "Beauty & Wellness",
    "Electronics",
    "FMCG",
    "Financial Services",
    "Food & Beverage (F&B)",
    "Health & Wellness",
    "Home Appliances",
    "Personal Care",
    "Property",
    "Real Estate",
    "Skincare",
    "Super App",
    "Technology",
    "Telecommunications",
    "Travel",
];

function toText(value: unknown) {
    return typeof value === "string" ? value.trim() : "";
}

function uniqueOptions(options: unknown[]) {
    return Array.from(
        new Set(options.map((option) => toText(option)).filter(Boolean)),
    );
}

function formatLegacyTag(tag?: string) {
    if (!tag) return "";

    return tag
        .split(" ")
        .map((word) =>
            word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : "",
        )
        .join(" ");
}

function getTagNames(tags?: unknown) {
    if (typeof tags === "string") {
        try {
            const parsedTags = JSON.parse(tags);

            if (Array.isArray(parsedTags)) {
                return getTagNames(parsedTags);
            }
        } catch {
            return [tags.trim()].filter(Boolean);
        }
    }

    if (!Array.isArray(tags)) {
        return [];
    }

    return tags
        .map((tag) => {
            if (typeof tag === "string") {
                return tag.trim();
            }

            if (tag && typeof tag === "object" && "name" in tag) {
                return toText((tag as WorkTag).name);
            }

            return "";
        })
        .filter(Boolean);
}

function getDisplayTags(work: WorkItem) {
    const tags = uniqueOptions([
        ...getTagNames(work.designTags),
        ...getTagNames(work.audienceTags),
    ]);

    return tags.length > 0 ? tags : [formatLegacyTag(work.tag)].filter(Boolean);
}

function normalizeWork(work: RawWork): WorkItem {
    return {
        id: work.id,
        ...(work.attributes ?? work),
    };
}

function normalizeWorks(works: unknown): WorkItem[] {
    if (!Array.isArray(works)) {
        return [];
    }

    return works
        .filter((work): work is RawWork => Boolean(work))
        .map((work) => normalizeWork(work));
}

function chunkWorks(works: WorkItem[]) {
    const chunks: WorkItem[][] = [];

    for (let index = 0; index < works.length; index += 2) {
        chunks.push(works.slice(index, index + 2));
    }

    return chunks;
}

function FilterSelect({
    value,
    options,
    onChange,
    className = "w-[8.75rem] md:w-auto",
}: {
    value: string;
    options: string[];
    onChange: (value: string) => void;
    className?: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handlePointerDown = (event: PointerEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
        };
    }, [isOpen]);

    const handleSelect = (option: string) => {
        onChange(option);
        setIsOpen(false);
    };

    return (
        <div
            ref={containerRef}
            className={`relative min-w-0 md:min-w-[7.75rem] ${className}`}
        >
            <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((current) => !current)}
                onKeyDown={(event) => {
                    if (event.key === "Escape") {
                        setIsOpen(false);
                    }
                }}
                className="flex h-8 w-full items-center justify-center gap-2 rounded-full border border-purple bg-green px-2.5 text-sm font-semibold text-purple outline-none transition-colors hover:bg-white/25 focus:bg-white/25 md:h-10 md:px-5"
            >
                <span className="min-w-0 truncate md:max-w-[11rem]">
                    {value}
                </span>
                <ChevronDownIcon
                    className={`h-4 w-4 shrink-0 text-purple transition-transform ${
                        isOpen ? "rotate-180" : "rotate-0"
                    }`}
                />
            </button>

            <div
                data-lenis-prevent
                onWheel={(event) => event.stopPropagation()}
                onTouchMove={(event) => event.stopPropagation()}
                className={`absolute bottom-full left-1/2 z-50 mb-2 max-h-[38vh] w-[min(15rem,calc(100vw-2rem))] -translate-x-1/2 overflow-auto rounded-2xl border border-purple bg-white p-2 shadow-xl shadow-darkGrey/15 transition-all duration-150 md:mb-3 md:max-h-[18rem] md:w-[min(18rem,calc(100vw-2rem))] md:min-w-full ${
                    isOpen
                        ? "pointer-events-auto translate-y-0 opacity-100 md:translate-y-0"
                        : "pointer-events-none translate-y-2 opacity-0 md:translate-y-2"
                }`}
            >
                {options.map((option) => (
                    <button
                        key={option}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={`block w-full rounded-xl px-3 py-2 text-left text-xs font-semibold transition-colors md:rounded-full md:px-4 md:text-sm ${
                            value === option
                                ? "bg-purple text-white"
                                : "text-purple hover:bg-[#F0E2FD]"
                        }`}
                    >
                        {option}
                    </button>
                ))}
            </div>
        </div>
    );
}

function ViewButton({
    children,
    isActive,
    onClick,
}: {
    children: React.ReactNode;
    isActive: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`h-11 min-w-[7.5rem] rounded-full border border-purple px-6 text-sm font-semibold transition-colors ${
                isActive
                    ? "bg-purple text-white"
                    : "bg-transparent text-purple hover:bg-purple hover:text-white"
            }`}
        >
            {children}
        </button>
    );
}

function WorkCard({ work, mode }: { work: WorkItem; mode: DisplayMode }) {
    const brandName = work.brandName || work.name || "";
    const projectName = work.name || "";
    const tags = getDisplayTags(work);
    const imageSrc = work.image?.url ? getStrapiMedia(work.image.url) : "";
    const href = work.slug ? `/works/${work.slug}` : "#";
    const isGrid = mode === "grid";

    return (
        <a
            href={href}
            className={`group block min-w-0 ${
                isGrid
                    ? ""
                    : "lg:flex-1 lg:transition-[flex] lg:duration-500 lg:ease-out lg:hover:flex-[2.08]"
            }`}
        >
            <div
                className={`relative overflow-hidden rounded-2xl bg-white ${
                    isGrid
                        ? "aspect-[1.64/1]"
                        : "aspect-[1.64/1] lg:aspect-[1.58/1]"
                }`}
            >
                {imageSrc ? (
                    <Image
                        src={imageSrc}
                        alt={work.image?.alternativeText || projectName}
                        fill
                        sizes={
                            isGrid
                                ? "(min-width: 1024px) 28vw, (min-width: 768px) 45vw, 90vw"
                                : "(min-width: 1024px) 50vw, 90vw"
                        }
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                ) : (
                    <div className="h-full w-full bg-white/70" />
                )}
            </div>

            <div className="pt-6">
                <h3 className="mb-2 text-[1.25rem] font-bold leading-tight text-darkGrey md:text-[1.45rem]">
                    {brandName}
                </h3>
                {/* {!isGrid && projectName && projectName !== brandName && ( */}
                <p className="mb-5 text-[1rem] text-darkGrey">{projectName}</p>
                {/* )} */}
                <div className="flex flex-wrap gap-3">
                    {tags.map((tag) => (
                        <span
                            key={tag}
                            className="rounded-full bg-white px-3 py-1.5 text-[0.7rem] font-semibold leading-none text-purple md:px-5 md:py-2 md:text-xs"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            </div>
        </a>
    );
}

export default function Works({ data }: { data: any }) {
    const [displayMode, setDisplayMode] = useState<DisplayMode>("stack");
    const [selectedDesign, setSelectedDesign] = useState(DESIGN_DEFAULT);
    const [selectedAudience, setSelectedAudience] = useState(AUDIENCE_DEFAULT);
    const [works, setWorks] = useState<WorkItem[]>(
        normalizeWorks(data?.works?.works),
    );

    const fetchData = useCallback(async () => {
        try {
            const timestamp = new Date().getTime(); // use timestamp to create unique url to resolve caching issues
            const data = await fetchAPI(`/works?_=${timestamp}`, "page");
            setWorks(normalizeWorks(data?.data));
        } catch (error) {
            console.error("Failed to fetch data:", error);
        }
    }, []);

    const updateUrlParam = (
        key: string,
        value: string,
        defaultValue: string,
    ) => {
        const url = new URL(window.location.href);
        if (value === defaultValue) {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, slugify(value));
        }
        window.history.pushState({}, "", url);
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const viewParam = urlParams.get("view");
        const designParam = urlParams.get("design");
        const audienceParam = urlParams.get("for");

        if (viewParam === "grid" || viewParam === "stack") {
            setDisplayMode(viewParam);
        }

        const allDesignOptions = uniqueOptions([
            DESIGN_DEFAULT,
            ...DESIGN_TAG_OPTIONS,
            ...works.flatMap((work) => getTagNames(work.designTags)),
            ...works.map((work) => formatLegacyTag(work.tag)),
        ]);
        const allAudienceOptions = uniqueOptions([
            AUDIENCE_DEFAULT,
            ...AUDIENCE_TAG_OPTIONS,
            ...works.flatMap((work) => getTagNames(work.audienceTags)),
        ]);

        const matchedDesign = allDesignOptions.find(
            (option) => slugify(option) === designParam,
        );
        const matchedAudience = allAudienceOptions.find(
            (option) => slugify(option) === audienceParam,
        );

        if (matchedDesign) setSelectedDesign(matchedDesign);
        if (matchedAudience) setSelectedAudience(matchedAudience);
    }, [works]);

    const designOptions = uniqueOptions([
        DESIGN_DEFAULT,
        ...DESIGN_TAG_OPTIONS,
        ...works.flatMap((work) => getTagNames(work.designTags)),
        ...works.map((work) => formatLegacyTag(work.tag)),
    ]);
    const audienceOptions = [
        AUDIENCE_DEFAULT,
        ...uniqueOptions([
            ...AUDIENCE_TAG_OPTIONS,
            ...works.flatMap((work) => getTagNames(work.audienceTags)),
        ]).sort((a, b) => a.localeCompare(b)),
    ];

    const filteredWorks =
        selectedDesign === DESIGN_DEFAULT &&
        selectedAudience === AUDIENCE_DEFAULT
            ? works
            : works.filter((work) => {
                  const designTags = getTagNames(work.designTags);
                  const audienceTags = getTagNames(work.audienceTags);
                  const legacyTag = formatLegacyTag(work.tag);
                  const matchesDesign =
                      selectedDesign === DESIGN_DEFAULT ||
                      designTags.includes(selectedDesign) ||
                      legacyTag === selectedDesign;
                  const matchesAudience =
                      selectedAudience === AUDIENCE_DEFAULT ||
                      audienceTags.includes(selectedAudience);

                  return matchesDesign && matchesAudience;
              });

    const handleDisplayModeChange = (mode: DisplayMode) => {
        setDisplayMode(mode);
        updateUrlParam("view", mode, "stack");
    };

    const handleDesignChange = (value: string) => {
        setSelectedDesign(value);
        updateUrlParam("design", value, DESIGN_DEFAULT);
    };

    const handleAudienceChange = (value: string) => {
        setSelectedAudience(value);
        updateUrlParam("for", value, AUDIENCE_DEFAULT);
    };
    return (
        <section className="relative bg-lightGrey px-8 pb-44 pt-32 md:pb-56 md:pt-[10vw] xl:px-[10.625vw] xl:pb-64">
            <RevealElement>
                <div className="space-y-10">
                    <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                        <h2 className="mb-0 text-left text-[1.65rem] font-semibold leading-tight text-darkGrey md:text-[1.9rem]">
                            Showing the latest {filteredWorks.length} projects
                        </h2>

                        <div className="hidden flex-wrap items-center gap-4 md:flex">
                            <span className="text-base font-semibold text-darkGrey">
                                Display
                            </span>
                            <ViewButton
                                isActive={displayMode === "stack"}
                                onClick={() => handleDisplayModeChange("stack")}
                            >
                                Stack View
                            </ViewButton>
                            <ViewButton
                                isActive={displayMode === "grid"}
                                onClick={() => handleDisplayModeChange("grid")}
                            >
                                Grid View
                            </ViewButton>
                        </div>
                    </div>

                    <div className="relative">
                        {displayMode === "grid" ? (
                            <div className="grid grid-cols-1 gap-x-6 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
                                {filteredWorks.map((work, index) => (
                                    <WorkCard
                                        key={work.id ?? `${work.slug}-${index}`}
                                        work={work}
                                        mode="grid"
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-12">
                                {chunkWorks(filteredWorks).map(
                                    (workPair, pairIndex) => (
                                        <div
                                            key={pairIndex}
                                            className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-6"
                                        >
                                            {workPair.map((work, index) => (
                                                <WorkCard
                                                    key={
                                                        work.id ??
                                                        `${work.slug}-${index}`
                                                    }
                                                    work={work}
                                                    mode="stack"
                                                />
                                            ))}
                                        </div>
                                    ),
                                )}
                            </div>
                        )}

                        {filteredWorks.length === 0 && (
                            <div className="rounded-2xl bg-white px-6 py-16 text-center">
                                <p className="mb-0 font-semibold text-darkGrey">
                                    No projects match these filters yet.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </RevealElement>
            <div className="fixed bottom-4 left-1/2 z-40 w-[calc(100%-2rem)] max-w-[33.5rem] -translate-x-1/2 rounded-[1.75rem] bg-green px-4 py-3 text-purple shadow-xl shadow-darkGrey/15 md:bottom-8 md:w-fit md:max-w-[calc(100%-4rem)] md:rounded-full md:px-8 md:py-2">
                <div className="flex flex-col items-center gap-2 md:hidden">
                    <div className="flex w-full items-center justify-center gap-3">
                        <span className="shrink-0 whitespace-nowrap text-base font-bold leading-none md:text-sm md:font-normal">
                            We Design
                        </span>
                        <FilterSelect
                            value={selectedDesign}
                            options={designOptions}
                            onChange={handleDesignChange}
                            className="w-[min(13.5rem,56vw)] md:w-auto"
                        />
                    </div>
                    <div className="flex w-full items-center justify-center gap-3">
                        <span className="shrink-0 whitespace-nowrap text-base font-bold leading-none md:text-sm md:font-normal">
                            for
                        </span>
                        <FilterSelect
                            value={selectedAudience}
                            options={audienceOptions}
                            onChange={handleAudienceChange}
                            className="w-[min(13.5rem,56vw)] md:w-auto"
                        />
                    </div>
                </div>
                <div className="hidden items-center justify-center gap-3 md:flex">
                    <span className="text-sm font-normal leading-none">
                        We Design
                    </span>
                    <FilterSelect
                        value={selectedDesign}
                        options={designOptions}
                        onChange={handleDesignChange}
                        className="w-auto"
                    />
                    <span className="text-sm font-normal leading-none">
                        for
                    </span>
                    <FilterSelect
                        value={selectedAudience}
                        options={audienceOptions}
                        onChange={handleAudienceChange}
                        className="w-auto"
                    />
                </div>
            </div>
            <BottomCurves
                color={data?.works?.waveBgColor?.waveBgColor}
                direction="right"
            />
        </section>
    );
}
